import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import SEO from './SEO';
import SchemaMarkup from './SchemaMarkup';
import PageHeader from './PageHeader';

export default function ServiceLandingLayout({ translationKey, proofLink }) {
  const { t, i18n } = useTranslation();
  const langPrefix = i18n.language === 'et' ? '/et' : '/en';
  const page = t(translationKey, { returnObjects: true });

  const items   = Array.isArray(page.solution_items) ? page.solution_items : [];
  const metrics = Array.isArray(page.metrics)        ? page.metrics        : [];
  const steps   = Array.isArray(page.process)        ? page.process        : [];
  const faqs    = Array.isArray(page.faq)            ? page.faq            : [];

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <SEO title={page.title} description={page.meta_desc} />
      <SchemaMarkup type="service" data={{ name: page.title, description: page.meta_desc }} />
      {faqs.length > 0 && <SchemaMarkup type="faq" data={{ faqs }} />}
      <PageHeader title={page.hero_title} subtitle={page.hero_subtitle} maxWidth="max-w-6xl" />

      <div className="container mx-auto px-4 py-12 md:py-20 max-w-6xl space-y-10">

        {/* Problem */}
        <div className="bg-slate-900 rounded-2xl p-8 md:p-12 border border-slate-800">
          <h2 className="text-2xl font-bold text-white mb-4">{page.problem_title}</h2>
          <p className="text-slate-300 text-lg leading-relaxed">{page.problem_desc}</p>
        </div>

        {/* Solution checklist + pricing */}
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">{page.solution_title}</h2>
          <div className="grid gap-4 sm:grid-cols-2 mb-8">
            {items.map((item, i) => (
              <div key={i} className="flex gap-3 items-start">
                <CheckCircleIcon className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{item}</span>
              </div>
            ))}
          </div>
          {page.price_from && (
            <div className="border-t border-slate-100 pt-6 flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-3xl font-black text-slate-900">{page.price_from}</span>
              {page.price_note && (
                <span className="text-slate-500 text-sm">{page.price_note}</span>
              )}
            </div>
          )}
        </div>

        {/* Process */}
        {steps.length > 0 && (
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">{page.process_title}</h2>
            <div className="grid gap-8 sm:grid-cols-2">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-4">
                  <span className="text-4xl font-black text-slate-100 leading-none select-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">{step.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Proof */}
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 shadow-sm">
          <div className="text-xs font-bold text-blue-700 uppercase tracking-widest mb-2">{page.proof_label}</div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">{page.proof_title}</h2>
          <p className="text-slate-600 leading-relaxed mb-8">{page.proof_desc}</p>
          <div className="grid grid-cols-3 gap-4 mb-8">
            {metrics.map((m, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                <div className="text-2xl font-extrabold text-slate-900">{m.value}</div>
                <div className="text-xs text-slate-500 mt-1">{m.label}</div>
              </div>
            ))}
          </div>
          <Link
            to={`${langPrefix}${proofLink}`}
            className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-500 transition-colors"
          >
            {page.proof_link} <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>

        {/* FAQ */}
        {faqs.length > 0 && (
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">{page.faq_title}</h2>
            <div className="divide-y divide-slate-100">
              {faqs.map((item, i) => (
                <div key={i} className="py-6 first:pt-0 last:pb-0">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.q}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-slate-900 rounded-2xl p-8 md:p-12 border border-slate-800 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">{page.cta_title}</h2>
          <p className="text-slate-300 mb-8">{page.cta_desc}</p>
          <Link
            to={`${langPrefix}/contact`}
            className="inline-flex items-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-full font-bold shadow-lg hover:bg-blue-500 hover:scale-105 transition-all duration-300"
          >
            {page.cta_btn} <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>

      </div>
    </div>
  );
}
